import { Select } from 'antd'
import classNames from 'classnames'
import React, { forwardRef, useEffect, useId, useMemo } from 'react'

import { isNull, toCapitalize } from '@app/utils'
import { useFetchedOptions } from '@app/hooks'
import { getStylesClassName } from '@app/utils/styles'

import { TAG_TYPE } from '@app/constants/tag'

import { LntTagRender } from './TagRender'
import { getFilteredValue, filterOptionsByLabel, getLabelsByValues } from './utils'

import styles from './styles.module.scss'

import type { LntSelectProps, LntSelectOption } from './types'
import type { RefSelectProps } from 'antd'
import type { LntTagRenderProps } from '@app/components/base/Select/TagRender/types'
import type { ForwardedRef, ReactElement } from 'react'

const DEFAULT_MAX_TAG_COUNT = 'responsive'

/**
 * @description
 *  strictValue - Чтобы в value не попадали данные, которых нет в Options (например, не валидные данные из квери строки)
 * */
const Component = <ValueType = unknown, QueryResponseResultType = unknown>(
  props: LntSelectProps<ValueType, QueryResponseResultType>,
  ref: ForwardedRef<RefSelectProps>
): ReactElement<LntSelectProps<ValueType, QueryResponseResultType>> => {
  const {
    tagRenderType = TAG_TYPE.primary,
    maxTagCount = DEFAULT_MAX_TAG_COUNT,
    strictValue,
    disabled,
    loading,
    children,
    mode,
    onTagClose,
    filterOption: outerFilterOptions,
    options: defaultOptions,
    querySelect: queryOptions,
    value: rawValue,
    className: outerClassName,
    allowClear = true,
    ...restProps
  } = props

  const id = useId()
  const { fetchedOptions, isLoading } = useFetchedOptions<LntSelectOption, QueryResponseResultType>({
    defaultOptions,
    queryOptions
  })

  const value = useMemo(
    () => (strictValue ? getFilteredValue(rawValue, fetchedOptions) : rawValue),
    [strictValue, rawValue, fetchedOptions]
  )

  const responsiveClassName: string | undefined = getStylesClassName(styles, {
    block: 'lntSelect',
    modificator: String(maxTagCount)
  })
  const modeClassName: string | undefined = getStylesClassName(styles, { block: 'lntSelect', modificator: mode })
  const modeTypeClassName: string | undefined = getStylesClassName(styles, {
    block: 'lntSelect',
    modificator: `${mode ?? ''}${toCapitalize(tagRenderType)}`
  })

  const className = classNames(
    styles.lntSelect,
    responsiveClassName,
    modeClassName,
    modeTypeClassName,
    { [styles.lntSelect_multipleDisabled]: disabled },
    outerClassName
  )

  const filterOption = outerFilterOptions ?? filterOptionsByLabel

  const tagRender = (customTagProps: LntTagRenderProps): JSX.Element => {
    return (
      <LntTagRender
        { ...customTagProps }
        type={ tagRenderType }
        disabled={ !!disabled }
        onTagClose={ onTagClose }
      />
    )
  }

  useEffect(
    function setTitleToRestTagInMultipleMode () {
      if (mode !== 'multiple') return

      const REST_TAG_SELECTOR = `[id='${id}'] .ant-select-selection-overflow-item-rest .ant-select-selection-item`
      const restTagElement = document.querySelector(REST_TAG_SELECTOR)

      if (isNull(restTagElement)) return

      const restTagElementText = restTagElement.textContent

      if (isNull(restTagElementText)) return

      const restCount = parseInt(restTagElementText.match(/\d+/)?.[0] ?? '')

      if (!Array.isArray(value) || isNaN(restCount) || restCount === 0) return

      const labels = getLabelsByValues(value.slice(-restCount), fetchedOptions)

      if (isNull(labels)) return

      restTagElement.setAttribute('title', labels.join(', '))
    },
    [mode, value, fetchedOptions]
  )

  return (
    <div id={ id }>
      <Select<ValueType>
        { ...restProps }
        value={ value as ValueType }
        disabled={ disabled }
        options={ fetchedOptions }
        className={ className }
        tagRender={ tagRender }
        loading={ isLoading || loading }
        filterOption={ filterOption }
        maxTagCount={ maxTagCount }
        mode={ mode }
        allowClear={ allowClear }
        ref={ ref }
      >
        { children }
      </Select>
    </div>
  )
}

export const LntSelect = forwardRef(Component) as <ValueType = unknown, QueryResponseResultType = unknown>(
  props: LntSelectProps<ValueType, QueryResponseResultType> & { ref?: ForwardedRef<RefSelectProps> }
) => ReactElement<LntSelectProps<ValueType, QueryResponseResultType>>
